import { gql } from 'graphql-tag';
import {
  ResourceRequestStatus,
  SpecificResourceRequestFragmentDoc
} from '~/types';

import {
  specificResourceAllocationFragment,
  specificResourceAllocationUserFragment,
  specificResourceAllocationTimeOffFragment,
  specificResourceAllocationHolidaysFragment,
  specificResourceRequestWithAllocationsFragment
} from '~/modules/resourcing/common/fragments';
import { useUpdateCompletedResourceAllocationProjectCacheOnAddWithProposedRequest2 } from '~/modules/projects/resourcing-plan/ResourceRequestChart/components/ResourceAllocationChart/hooks/useUpdateCompletedResourceAllocationCache';

import {
  mapToServiceRequest,
  mapToServiceRequestWithTimeoff
} from '../../util';

export const COMPLETE_RESOURCE_REQUEST = gql`
  mutation markResourceRequestAsComplete(
    $input: MarkResourceRequestAsCompleteInput!
  ) {
    markResourceRequestAsComplete2(input: $input) {
      resourceRequest {
        ...SpecificResourceRequestWithAllocation
      }
      isOptimistic @client
    }
  }
  ${specificResourceRequestWithAllocationsFragment}
`;

export const COMPLETE_RESOURCE_REQUEST_WITHOUT_DIRECTIVE = gql`
  mutation markResourceRequestAsComplete(
    $input: MarkResourceRequestAsCompleteInput!
  ) {
    markResourceRequestAsComplete2(input: $input) {
      resourceRequest {
        ...SpecificResourceRequestWithAllocation
      }
    }
  }
  ${specificResourceRequestWithAllocationsFragment}
`;

export const COMPLETE_RESOURCE_REQUEST_WITH_TIMEOFF = gql`
  mutation markResourceRequestAsComplete(
    $input: MarkResourceRequestAsCompleteInput!
    $showTimeOff: Boolean!
    $chartDateRange: DateRangeInput
    $showHolidays: Boolean!
  ) {
    markResourceRequestAsComplete2(input: $input) {
      resourceRequest {
        ...SpecificResourceRequest
        resourceAllocations {
          ...SpecificResourceAllocation
          role {
            uri
            id
            displayText
          }
          user {
            ...SpecificResourceAllocationUser
            ...SpecificResourceAllocationTimeOff
            ...SpecificResourceAllocationHolidays
          }
        }
      }
      isOptimistic @client
    }
  }
  ${SpecificResourceRequestFragmentDoc}
  ${specificResourceAllocationFragment}
  ${specificResourceAllocationUserFragment}
  ${specificResourceAllocationTimeOffFragment}
  ${specificResourceAllocationHolidaysFragment}
`;

export const COMPLETE_RESOURCE_REQUEST_WITH_TIMEOFF_WITHOUT_DIRECTIVE = gql`
  mutation markResourceRequestAsComplete(
    $input: MarkResourceRequestAsCompleteInput!
    $showTimeOff: Boolean!
    $chartDateRange: DateRangeInput
    $showHolidays: Boolean!
  ) {
    markResourceRequestAsComplete2(input: $input) {
      resourceRequest {
        ...SpecificResourceRequest
        resourceAllocations {
          ...SpecificResourceAllocation
          role {
            uri
            id
            displayText
          }
          user {
            ...SpecificResourceAllocationUser
            ...SpecificResourceAllocationTimeOff
            ...SpecificResourceAllocationHolidays
          }
        }
      }
    }
  }
  ${SpecificResourceRequestFragmentDoc}
  ${specificResourceAllocationFragment}
  ${specificResourceAllocationUserFragment}
  ${specificResourceAllocationTimeOffFragment}
  ${specificResourceAllocationHolidaysFragment}
`;

export const getCompleteResourceRequestQueries = () => {
  return {
    mutationDocument: COMPLETE_RESOURCE_REQUEST_WITHOUT_DIRECTIVE,
    mutationWithTimeoffDocument: COMPLETE_RESOURCE_REQUEST_WITH_TIMEOFF_WITHOUT_DIRECTIVE
  };
};

export const invokeMarkResourceRequestAsComplete = markResourceRequestAsComplete => ({
  allocationChartRef,
  chartDateRange,
  chartSelectedTab,
  cacheUpdateFunctions,
  request,
  requestAccessLevel,
  showHolidays,
  showTimeOff
}) => {
  const requestParams = {
    request,
    responseType: 'markResourceRequestAsComplete2',
    responseTypeName: 'MarkResourceRequestAsCompleteResult',
    requestStatus: ResourceRequestStatus.Complete,
    chartSelectedTab,
    requestAccessLevel,
    refetchQueries: ['getProjectTotalsBySlug']
  };

  allocationChartRef?.current.setResourceRequestAcceptedLoading(true);

  if (!showTimeOff && !showHolidays) {
    markResourceRequestAsComplete(
      mapToServiceRequest({
        ...requestParams,
        cacheUpdate: useUpdateCompletedResourceAllocationProjectCacheOnAddWithProposedRequest2(
          {
            allocationChartRef
          }
        ),
        cacheUpdateFunctions
      })
    );
  } else {
    markResourceRequestAsComplete(
      mapToServiceRequestWithTimeoff({
        ...requestParams,
        cacheUpdate: useUpdateCompletedResourceAllocationProjectCacheOnAddWithProposedRequest2(
          {
            allocationChartRef
          }
        ),
        chartDateRange,
        showTimeOff,
        showHolidays,
        allocationChartRef,
        cacheUpdateFunctions
      })
    );
  }
};
